import React from 'react';
import { Controller } from 'react-hook-form';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import {
  DeferredCompensationContributionUnit,
  DeferredCompensationDTO,
} from '../../tables/DeferredCompensation/DeferredCompensation.meta';
import { useDeferredCompensationForm } from './useDeferredCompensationForm';
import {
  DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS,
  DeferredCompensationFormValues,
  PRINCIPAL_ID,
} from './DeferredCompensationForm.meta';
import { UPSERT_ASSET_FORM_ID } from '../../components/UpsertAssetModal/UpsertAssetModal.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import { AssetBucketSelect } from '../components/AssetBucketSelect/AssetBucketSelect';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';

export interface Props {
  clientId: string;
  asset: DeferredCompensationDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
  isEstatePlanningWorkflow: boolean;
}

export const DeferredCompensationForm = ({
  clientId,
  options,
  asset,
  onSuccess,
  isEstatePlanningWorkflow,
}: Props) => {
  const {
    data: {
      control,
      errors,
      principalEarnerAge,
      spouseEarnerAge,
      contributionUnit,
      defaultContributionUnitOption,
      contributionStartAgeOptions,
      distributionStartAgeOptions,
      contributionEndYearOptions,
      distributionPeriodOptions,
      assetBucketOptions,
      initialPrincipalDistributionStartAgeValue,
      initialSpouseDistributionStartAgeValue,
      isNewDeferredCompensation,
      assetBucketingByManagementStyle,
      managementStylesOptions,
      onManagementSelect,
      watchedManagementStyleSelection,
      allowAssetBucketingCustomization,
      displayAssetBucketing,
    },
    actions: { onSubmit, register, setValue },
  } = useDeferredCompensationForm({ clientId, options, asset, onSuccess });

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_ASSET_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.space['16']}px;
      `}
      aria-label="deferred compensation form"
    >
      <Input
        label="Description"
        formVariant={errors.name && 'error'}
        helperText={errors.name?.message}
        {...register('name')}
      />

      <Controller
        name="earner"
        control={control}
        render={({ field: { value, onChange, ref } }) => (
          <Select
            label="Earner"
            formVariant={errors.earner && 'error'}
            helperText={errors.earner?.message}
            options={options.DeferredCompensationOptions.earner}
            value={options.DeferredCompensationOptions.earner.find(
              option => option.value === value,
            )}
            onChange={option => {
              onChange(option?.value);

              const isPrincipal = option?.value === PRINCIPAL_ID;

              // reset dependent fields
              setValue('contributionStartAge', isPrincipal ? principalEarnerAge : spouseEarnerAge);
              setValue('distributionPeriod', 1);
              setValue('contributionEndYear', null);

              if (isNewDeferredCompensation) {
                setValue(
                  'distributionStartAge',
                  isPrincipal
                    ? initialPrincipalDistributionStartAgeValue
                    : initialSpouseDistributionStartAgeValue,
                );
              }
            }}
            innerRef={ref}
          />
        )}
      />

      <Controller
        control={control}
        name="value"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            decimalScale={2}
            allowNegative={false}
            label="Current Value"
            leftIcon="far fa-dollar-sign"
            formVariant={errors.value && 'error'}
            helperText={errors.value?.message}
            onValueChange={({ floatValue }) => {
              onChange(floatValue ?? null);
            }}
            {...field}
          />
        )}
      />

      <Controller
        control={control}
        name="yearlyContribution"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            decimalScale={
              contributionUnit === DeferredCompensationContributionUnit.Dollar ? 2 : undefined
            }
            allowNegative={false}
            label="Annual Contribution"
            formVariant={errors.yearlyContribution && 'error'}
            helperText={errors.yearlyContribution?.message}
            onValueChange={({ floatValue }) => {
              onChange(floatValue ?? null);
            }}
            selectorProps={{
              defaultValue: defaultContributionUnitOption,
              options: DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS,
              onChange: option => setValue('contributionUnit', option.value),
              ariaLabel: 'Annual contribution unit selector',
            }}
            {...field}
          />
        )}
      />

      <Box
        css={`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${({ theme }) => theme.space['16']}px;
        `}
      >
        <Controller
          name="contributionStartAge"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              isPortal
              label="Contribution Start Age"
              formVariant={errors.contributionStartAge && 'error'}
              helperText={errors.contributionStartAge?.message}
              options={contributionStartAgeOptions}
              value={contributionStartAgeOptions.find(option => option.value === value)}
              onChange={option => onChange(option?.value)}
              innerRef={ref}
            />
          )}
        />

        <Controller
          name="contributionEndYear"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              isPortal
              label="Contribution End Year"
              formVariant={errors.contributionEndYear && 'error'}
              helperText={errors.contributionEndYear?.message}
              options={contributionEndYearOptions}
              value={contributionEndYearOptions.find(option => option.value === value)}
              onChange={option => {
                onChange(option?.value);

                // reset dependent fields
                setValue('distributionStartAge', null);
              }}
              innerRef={ref}
            />
          )}
        />

        <Controller
          name="distributionStartAge"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              isPortal
              label="Distribution Start Age"
              formVariant={errors.distributionStartAge && 'error'}
              helperText={errors.distributionStartAge?.message}
              options={distributionStartAgeOptions}
              value={distributionStartAgeOptions.find(option => option.value === value) || null}
              onChange={option => {
                onChange(option?.value);

                // reset dependent fields
                setValue('distributionPeriod', 1);
              }}
              innerRef={ref}
            />
          )}
        />

        <Controller
          name="distributionPeriod"
          control={control}
          render={({ field: { value, onChange, ref } }) => (
            <Select
              isPortal
              label="Distribution Years"
              formVariant={errors.distributionPeriod && 'error'}
              helperText={errors.distributionPeriod?.message}
              options={distributionPeriodOptions}
              value={distributionPeriodOptions.find(option => option.value === value)}
              onChange={option => onChange(option?.value)}
              innerRef={ref}
            />
          )}
        />

        <Controller
          control={control}
          name="accumulationGrowthRate"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              allowNegative={false}
              label="Accumulation Growth Rate"
              rightIcon="far fa-percent"
              formVariant={errors.accumulationGrowthRate && 'error'}
              helperText={errors.accumulationGrowthRate?.message}
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          name="distributionGrowthRate"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              allowNegative={false}
              label="Distribution Growth Rate"
              rightIcon="far fa-percent"
              formVariant={errors.distributionGrowthRate && 'error'}
              helperText={errors.distributionGrowthRate?.message}
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              {...field}
            />
          )}
        />
      </Box>

      {assetBucketingByManagementStyle && (
        <ManagementStyle<DeferredCompensationFormValues>
          control={control}
          options={managementStylesOptions}
          formKey="orionManagementStyleId"
          onManagementSelect={onManagementSelect}
        />
      )}

      {displayAssetBucketing && (
        <AssetBucketSelect<DeferredCompensationFormValues>
          control={control}
          formKey="customBucketIndex"
          isDisabled={
            (assetBucketingByManagementStyle && !allowAssetBucketingCustomization) ||
            (assetBucketingByManagementStyle && watchedManagementStyleSelection === null)
          }
          options={assetBucketOptions}
        />
      )}

      {isEstatePlanningWorkflow && (
        <Box>
          <Text as="label" variant="formLabel" color="neutral.600">
            Liquidity Status
          </Text>

          <Box display="flex" flexDirection="column" gridGap="8" mb="8">
            <Box>
              <Controller
                name="isLiquid"
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <>
                    <Radio name={name} checked={value} onChange={() => onChange(true)}>
                      Liquid
                    </Radio>
                    <Radio name={name} checked={!value} onChange={() => onChange(false)} mr="4">
                      Illiquid
                    </Radio>
                  </>
                )}
              />
            </Box>
          </Box>
        </Box>
      )}
    </form>
  );
};
